import { useEffect, useRef } from "react";
import heroIllustration from "../../assets/images/hero-illustration.jpg";

type ParticleShape = "triangle" | "square" | "circle" | "line";

class Particle {
  x: number;
  y: number;
  size: number;
  speedX: number;
  speedY: number;
  rotation: number;
  rotationSpeed: number;
  shape: ParticleShape;

  constructor(canvas: HTMLCanvasElement) {
    this.x = Math.random() * canvas.width;
    this.y = Math.random() * canvas.height;
    this.size = Math.random() * 20 + 10;
    this.speedX = (Math.random() - 0.5) * 0.5;
    this.speedY = (Math.random() - 0.5) * 0.5;
    this.rotation = Math.random() * Math.PI * 2;
    this.rotationSpeed = (Math.random() - 0.5) * 0.02;
    const shapes: ParticleShape[] = ["triangle", "square", "circle", "line"];
    this.shape = shapes[Math.floor(Math.random() * shapes.length)];
  }

  update(canvas: HTMLCanvasElement) {
    this.x += this.speedX;
    this.y += this.speedY;
    this.rotation += this.rotationSpeed;

    if (this.x > canvas.width) this.x = 0;
    if (this.x < 0) this.x = canvas.width;
    if (this.y > canvas.height) this.y = 0;
    if (this.y < 0) this.y = canvas.height;
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.rotation);
    ctx.strokeStyle = "rgba(239, 68, 68, 0.3)";
    ctx.lineWidth = 2;

    switch (this.shape) {
      case "triangle":
        ctx.beginPath();
        ctx.moveTo(0, -this.size / 2);
        ctx.lineTo(this.size / 2, this.size / 2);
        ctx.lineTo(-this.size / 2, this.size / 2);
        ctx.closePath();
        ctx.stroke();
        break;
      case "square":
        ctx.strokeRect(-this.size / 2, -this.size / 2, this.size, this.size);
        break;
      case "circle":
        ctx.beginPath();
        ctx.arc(0, 0, this.size / 2, 0, Math.PI * 2);
        ctx.stroke();
        break;
      case "line":
        ctx.beginPath();
        ctx.moveTo(-this.size / 2, 0);
        ctx.lineTo(this.size / 2, 0);
        ctx.stroke();
        break;
    }

    ctx.restore();
  }
}

function Hero() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollToContent = () => {
    const target = document.querySelector("#services");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const particles: Particle[] = Array.from(
      { length: 30 },
      () => new Particle(canvas),
    );

    let animationId = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((particle) => {
        particle.update(canvas);
        particle.draw(ctx);
      });

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <section className="relative flex min-h-[80vh] items-center justify-center overflow-hidden bg-white px-6 pt-16 md:min-h-screen md:px-12 md:pt-20 lg:px-24">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 h-full w-full"
        style={{ zIndex: 0 }}
      />
      <div className="absolute inset-0 opacity-10" style={{ zIndex: 1 }}>
        <div className="animate-grid-move absolute inset-0 bg-[linear-gradient(to_right,#ef444420_1px,transparent_1px),linear-gradient(to_bottom,#ef444420_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
      </div>
      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 md:gap-16 lg:grid-cols-2">
        <div className="fade-in-section">
          <h1 className="mb-6 text-4xl leading-tight font-bold text-gray-900 md:mb-8 md:text-7xl">
            あなたのビジネスを
            <br />
            <span className="text-red-500">デジタル</span>で加速
          </h1>
          <p className="mb-8 text-base leading-relaxed text-gray-600 md:mb-12 md:text-xl">
            Webサイト制作からアプリ開発まで幅広く対応。
            <br />
            あなたのアイデアを形にします。
          </p>
          <div className="grid max-w-xl grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:gap-4">
            <a
              href="#contact"
              className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-red-500 px-4 py-3 text-sm font-semibold whitespace-nowrap text-white transition-all duration-300 hover:bg-red-600 hover:shadow-lg hover:shadow-red-500/30 sm:px-8 sm:py-4 sm:text-base"
            >
              無料相談する
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="#services"
              className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-red-500 bg-transparent px-4 py-3 text-sm font-semibold whitespace-nowrap text-red-500 transition-all duration-300 hover:bg-red-500 hover:text-white sm:px-8 sm:py-4 sm:text-base"
            >
              サービスを見る
            </a>
          </div>
        </div>
        <div className="fade-in-section relative">
          <div className="animate-float relative">
            <img
              src={heroIllustration}
              alt="IT Development Illustration"
              className="h-auto w-full rounded-2xl"
            />
          </div>
        </div>
      </div>
      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2 text-gray-500 md:bottom-8">
        <button
          type="button"
          onClick={scrollToContent}
          className="flex h-12 w-12 animate-bounce cursor-pointer items-center justify-center rounded-full border-2 border-red-200 bg-white/80 text-red-500 shadow-md backdrop-blur-sm"
          aria-label="Scroll to content"
        >
          <i className="ri-arrow-down-line text-xl"></i>
        </button>
        <span className="text-xs font-medium tracking-wide">Scroll</span>
      </div>
    </section>
  );
}

export default Hero;
